import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import styles from './Estimate.module.scss';
import classNames from 'classnames/bind';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import EstimateTemplateList from '~/components/templates/estimate/list-estimate/EstimateList';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { IPayloadDeleteTemplates } from '~/utils/interface/template';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import {
  handleGetEstimateTemplate,
  handleGetEstimateTemplates,
} from '~/thunks/estimate-template/estimateTemplateThunk';
import EditEstimateTemplateDialog from '~/components/templates/estimate/edit-estimate-template-dialog/EditEstimateTemplateDialog';
import { estimateTemplateActions } from '~/thunks/estimate-template/estimateTemplateSlice';
import estimateTemplateApi from '~/apis/estimate-template';
import { IResponseGetEstimateTemplate } from '~/utils/interface/estimate-template';

const cx = classNames.bind(styles);

const TemplateEstimate = () => {
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const { t } = useTranslation('admin/estimate-template');
  const [editData, setEditData] = useState<IResponseGetEstimateTemplate>();
  const [openEditTemplateDialog, setOpenEditTemplateDialog] = useState<boolean>(false);
  const [editTemplateDialogMode, setEditTemplateDialogMode] = useState<string>('');
  const { filter, selectedIds, needRefreshData, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.estimateTemplate
  );
  const { profile } = useAppSelector((state) => state.auth);
  const initValues: IResponseGetEstimateTemplate = {
    id: 0,
    total: 0,
    subtotal: 0,
    name: '',
    tax: profile.tax ?? 0,
    estimateItems: [],
    estimateFiles: [],
  };
  useEffect(() => {
    dispatch(handleGetEstimateTemplates({ data: { ...filter } }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  const openAlertDeleteModal = useCallback(() => {
    dispatch(estimateTemplateActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const handleClickTemplate = async () => {
    setEditData(initValues);
    setEditTemplateDialogMode(DIALOG_MODE_CREATE);
    setOpenEditTemplateDialog(true);
  };

  const handleSetOpen = async (open: boolean, needRefreshData?: boolean) => {
    setOpenEditTemplateDialog(open);
    if (needRefreshData) dispatch(estimateTemplateActions.changeNeedRefreshData(true));
  };

  const handleOpenEdit = async (id: string) => {
    try {
      const response = await dispatch(handleGetEstimateTemplate(id)).unwrap();
      setEditData(response);
      setEditTemplateDialogMode(DIALOG_MODE_EDIT);
      setOpenEditTemplateDialog(true);
    } catch (e) {
      handleApiError(e);
    }
  };

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(estimateTemplateActions.changeMenuSelectId(undefined));
    dispatch(estimateTemplateActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedTemplates = useCallback(async () => {
    let payloadDeleteTemplates: IPayloadDeleteTemplates =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await estimateTemplateApi.deleteEstimateTemplate(payloadDeleteTemplates);
      notify('success', t('deleteSuccessMessage', { ns: 'admin/common' }));
      dispatch(estimateTemplateActions.changeNeedRefreshData(true));
      dispatch(estimateTemplateActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  return (
    <Box className='w-full h-full'>
      <Box className='flex flex-row'>
        <BaseHeaderTitle title={t('estimateTemplate')} />
        <Box>
          {permissions[PERMISSIONS.estimateTemplate_delete] && (
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
          )}
          {permissions[PERMISSIONS.estimateTemplate_create] ? (
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-lg ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleClickTemplate}
            >
              {t('addTemplate')}
            </Button>
          ) : (
            <></>
          )}
        </Box>
      </Box>

      <Box className='mt-[40px]'>
        {permissions[PERMISSIONS.estimateTemplate_view] ? <EstimateTemplateList onOpenEdit={handleOpenEdit} /> : null}
      </Box>

      <EditEstimateTemplateDialog
        data={editData}
        open={openEditTemplateDialog}
        setOpen={handleSetOpen}
        mode={editTemplateDialogMode}
        editData={editData}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedTemplates}
      />
    </Box>
  );
};

export default TemplateEstimate;
